import { useContext, useEffect, useState } from 'react';
import PluginsContext from '../../../context/PluginsContext';
import { useTranslation } from 'react-i18next';
import {
  Grid, TextField, Box, FormControlLabel,
  Snackbar, Alert, Typography, Select, MenuItem, FormControl, InputLabel, Paper,
  List, ListItemButton, ListItemIcon, ListItemText
} from '@mui/material';
import { AccountBalanceWalletOutlined, BusinessOutlined, CurrencyExchangeOutlined, ExtensionOutlined, PointOfSaleOutlined, ReceiptLongOutlined, StorefrontOutlined, TuneOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { getSettings, updateSettings } from '../../../services/SettingsService';
import { getLocations } from '../../../services/LocationService';
import { getBusinessUnits } from '../../../services/BusinessUnitService';
import IOSSwitch from '../../../components/Switch/IOSSwitch';
import LogoUpload from '../../../components/Settings/LogoUpload';
import PosIntegrationSettingsTab from '../../../components/Settings/PosIntegrationSettingsTab';
import PluginsSettingsTab from '../../../components/Settings/PluginsSettingsTab';
import CurrencySettingsTab from '../../../components/Settings/CurrencySettingsTab';
import SettingsContentCard from '../../../components/Settings/SettingsContentCard';
import './SettingsPage.scss';

const ELECTRONIC_BILLING_PROVIDER = 'FACTURA_LISTA';

const FACTURA_LISTA_BILLING_INDICATOR_OPTIONS = [
  { value: '3', label: '3 - IVA básica' },
  { value: '2', label: '2 - IVA mínima' },
  { value: '1', label: '1 - No gravado' },
  { value: '16', label: '16 - Literal E' }
];

const INTEGRATIONS = {
  mercadopago: {
    title: 'Mercado Pago',
    fields: [
      {
        key: 'mercadopago_user_id',
        label: 'User ID',
        disabled: true,
        placeholder: 'Se autogenera al guardar con el Access Token',
        helperText: 'No es necesario cargarlo manualmente.'
      },
      { key: 'mercadopago_application_number', label: 'Client ID' },
      { key: 'mercadopago_public_key', label: 'Public Key' },
      { key: 'mercadopago_access_token', label: 'Access Token' }
    ]
  },
};

const SettingsPage = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({});
  const [locations, setLocations] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]').map(Number);
  const canModifySettings = isSuperAdmin
    || permissions.includes(821);
  const canManageCompanyIdentity = canModifySettings || permissions.includes(825);
  const canManagePlugins = canModifySettings || permissions.includes(826);
  const { plugins } = useContext(PluginsContext);
  // Monedas es parte del plugin "Sistema multimoneda": sin habilitarlo no aparece (la empresa opera solo en pesos).
  const multiCurrencyEnabled = plugins.some((plugin) => plugin.key === 'multi_currency' && plugin.enabled);
  const [activeSection, setActiveSection] = useState('company');
  const [selectedLocationId, setSelectedLocationId] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (isSuperAdmin) {
          const settingsData = await getSettings();
          const formattedSettings = {};
          settingsData.forEach(({ key, value }) => {
            formattedSettings[key] = value;
          });
          setSettings(formattedSettings);
        }

        if (canModifySettings) {
          const locationData = await getLocations();
          const branchLocations = (locationData || []).filter((location) => !location.isPlant);
          setLocations(branchLocations);
          setSelectedLocationId(String(branchLocations[0]?.id || ''));
        }

        if (isSuperAdmin) {
          try {
            const businessUnitsData = await getBusinessUnits();
            setBusinessUnits(businessUnitsData || []);
          } catch (error) {
            console.error('Error loading business units:', error);
            setBusinessUnits([]);
          }
        }
      } catch (error) {
        console.error('Error loading settings or locations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isSuperAdmin, canModifySettings]);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => { 
    const payload = {
      settings: Object.entries(settings).map(([key, value]) => ({
        key,
        value: value || ''
      }))
    };

    setSaving(true);
    const success = await updateSettings(payload);
    setSaving(false);

    if (success) {
      setSuccessOpen(true);
    } else {
      console.error('No se pudo guardar la configuración');
    }
  };

  const handleSalesViewModeChange = async (locationId, value) => {
    const key = `location_sales_view_mode_${locationId}`;
    handleChange(key, value);
    setSaving(true);
    const success = await updateSettings({ settings: [{ key, value }] });
    setSaving(false);

    if (success) {
      setSuccessOpen(true);
    } else {
      console.error('No se pudo guardar el modo de venta');
    }
  };

  const renderIntegrationCard = (integration, config) => {
    if (integration === 'mercadopago') {
      const mercadoPagoScopes = [
        { id: null, name: 'Main' },
        ...businessUnits
          .filter((unit) => unit.type === 'FRANCHISE')
          .map((unit) => ({ id: unit.id, name: unit.name }))
      ];
      const scopedKey = (baseKey, businessUnitId) =>
        businessUnitId ? `${baseKey}_business_unit_${businessUnitId}` : baseKey;

      return mercadoPagoScopes.map((scope) => {
        const activeKey = scopedKey('mercadopago_active', scope.id);
        const isActive = settings[activeKey] === 'true';
        return (
          <SettingsContentCard key={`mercadopago_${scope.id || 'main'}`}
            sx={{ mt: 3 }}
            title={`Mercado Pago · ${scope.name}`}
            description={scope.id ? 'Cuenta de cobro utilizada por las sucursales de esta franquicia.' : 'Cuenta de cobro utilizada por las sucursales principales.'}
            action={
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={isActive}
                      onChange={(e) => handleChange(activeKey, e.target.checked.toString())}
                    />
                  }
                />
              }
          >
              {isActive && (
                <Grid container spacing={2}>
                  {config.fields.map(({ key, label, disabled, placeholder, helperText }) => {
                    const fieldKey = scopedKey(key, scope.id);
                    return (
                      <Grid item xs={12} md={6} key={fieldKey}>
                        <TextField
                          label={label}
                          fullWidth
                          type={key === 'mercadopago_access_token' ? 'password' : 'text'}
                          value={settings[fieldKey] || ''}
                          disabled={disabled}
                          placeholder={placeholder}
                          helperText={helperText}
                          onChange={(e) => handleChange(fieldKey, e.target.value)}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}
          </SettingsContentCard>
        );
      });
    }

    if (integration === 'electronic_billing') {
      return locations.filter(loc => String(loc.id) === String(selectedLocationId)).map((location) => {
        const id = location.id;
        const name = location.name;
        const providerKey = `electronic_billing_provider_location_${id}`;
        const idEmisorKey = `electronic_billing_factura_lista_id_emisor_location_${id}`;
        const codSucursalKey = `electronic_billing_factura_lista_cod_sucursal_location_${id}`;
        const usuarioKey = `electronic_billing_factura_lista_usuario_location_${id}`;
        const passwordKey = `electronic_billing_factura_lista_password_location_${id}`;
        const billingIndicatorKey = `electronic_billing_factura_lista_billing_indicator_location_${id}`;
        // Al cargar cualquier dato de facturación queda seleccionado el proveedor de la sucursal.
        const handleBillingChange = (key, value) => {
          handleChange(key, value);
          if (settings[providerKey] !== ELECTRONIC_BILLING_PROVIDER) {
            handleChange(providerKey, ELECTRONIC_BILLING_PROVIDER);
          }
        };

        return (
          <SettingsContentCard key={id} sx={{ mt: 3 }} title="Facturación electrónica" description={`Configuración de emisión para ${name}. Datos del eConector de Factura Lista.`}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="ID Emisor"
                  fullWidth
                  value={settings[idEmisorKey] || ''}
                  onChange={(e) => handleBillingChange(idEmisorKey, e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Código sucursal"
                  fullWidth
                  value={settings[codSucursalKey] || ''}
                  onChange={(e) => handleBillingChange(codSucursalKey, e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Usuario"
                  fullWidth
                  value={settings[usuarioKey] || ''}
                  onChange={(e) => handleBillingChange(usuarioKey, e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Password"
                  fullWidth
                  type="password"
                  value={settings[passwordKey] || ''}
                  onChange={(e) => handleBillingChange(passwordKey, e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Indicador de facturación</InputLabel>
                  <Select
                    label="Indicador de facturación"
                    value={settings[billingIndicatorKey] || '3'}
                    onChange={(e) => handleBillingChange(billingIndicatorKey, e.target.value)}
                  >
                    {FACTURA_LISTA_BILLING_INDICATOR_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </SettingsContentCard>
        );
      });
    }

    if (integration === 'shopping') {
      return locations.filter(loc => String(loc.id) === String(selectedLocationId)).map((location) => {
        const id = location.id;
        const name = location.name;
        const activeKey = `shopping_active_location_${id}`;
        const rutKey = `shopping_rut_location_${id}`;
        const passwordKey = `shopping_password_location_${id}`;
        const shoppingCodeKey = `shopping_shoppingCode_location_${id}`;
        const contractNumberKey = `shopping_contractNumber_location_${id}`;
        const wsdlUrlKey = `shopping_wsdlUrl_location_${id}`;
        const rubroCodeKey = `shopping_rubroCode_location_${id}`;
        const isActive = settings[activeKey] === 'true';

        return (
          <SettingsContentCard key={`shopping_${id}`} sx={{ mt: 3 }} title="Declaración de ventas" description={`Datos de conexión para declarar las ventas de ${name} al shopping.`} action={
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={isActive}
                      onChange={(e) => handleChange(activeKey, e.target.checked.toString())}
                    />
                  }
                />
              }>
              {isActive && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="RUT"
                      fullWidth
                      value={settings[rutKey] || ''}
                      onChange={(e) => handleChange(rutKey, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Password"
                      fullWidth
                      type="password"
                      value={settings[passwordKey] || ''}
                      onChange={(e) => handleChange(passwordKey, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Shopping Code"
                      fullWidth
                      value={settings[shoppingCodeKey] || ''}
                      onChange={(e) => handleChange(shoppingCodeKey, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Contract Number"
                      fullWidth
                      value={settings[contractNumberKey] || ''}
                      onChange={(e) => handleChange(contractNumberKey, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="WSDL URL"
                      fullWidth
                      value={settings[wsdlUrlKey] || ''}
                      onChange={(e) => handleChange(wsdlUrlKey, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Rubro Code"
                      fullWidth
                      value={settings[rubroCodeKey] || ''}
                      onChange={(e) => handleChange(rubroCodeKey, e.target.value)}
                    />
                  </Grid>
                </Grid>
              )}
          </SettingsContentCard>
        );
      });
    }

    const { title, fields } = config;
    const activeKey = `${integration}_active`;
    const isActive = settings[activeKey] === 'true';

    return (
      <SettingsContentCard key={integration} sx={{ mt: 3 }} title={title} action={
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={isActive}
                  onChange={(e) => handleChange(activeKey, e.target.checked.toString())}
                />
              }
            />
          }>
          {isActive && (
            <Grid container spacing={2}>
              {fields.map(({ key, label }) => (
                <Grid item xs={12} md={6} key={key}>
                  <TextField
                    label={label}
                    fullWidth
                    value={settings[key] || ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
      </SettingsContentCard>
    );
  };

  const selectedLocation = locations.find((location) => String(location.id) === String(selectedLocationId));
  const isBranchSection = ['branches', 'shopping', 'pos'].includes(activeSection);
  const needsLocationSelector = ['branches', 'shopping', 'pos'].includes(activeSection);
  const allNavigation = [
    { id: 'company', label: 'Empresa', helper: 'Identidad y preferencias', icon: <BusinessOutlined /> },
    { id: 'mercadopago', label: 'Mercado Pago', helper: 'Cuentas de cobro', icon: <AccountBalanceWalletOutlined /> },
    { id: 'shopping', label: 'Shopping', helper: 'Declaración de ventas', icon: <StorefrontOutlined /> },
    { id: 'branches', label: 'Facturación', helper: 'Datos por sucursal', icon: <TuneOutlined /> },
    { id: 'pos', label: 'POS', helper: 'Terminales de cobro', icon: <PointOfSaleOutlined /> },
    { id: 'currencies', label: 'Monedas', helper: 'Pesos, dólares y cotización', icon: <CurrencyExchangeOutlined /> },
    { id: 'plugins', label: 'Plugins', helper: 'Funcionalidades activas', icon: <ExtensionOutlined /> }
  ];
  const visibleNavigation = allNavigation.filter((item) => item.id !== 'currencies' || multiCurrencyEnabled);
  const navigation = isSuperAdmin
    ? visibleNavigation
    : visibleNavigation.filter((item) => (
      item.id === 'company'
      || (item.id === 'pos' && canModifySettings)
      || (item.id === 'currencies' && canModifySettings)
      || (item.id === 'plugins' && canManagePlugins)
    ));
  const sectionDescriptions = {
    company: 'Configurá la identidad que representa a tu empresa.',
    mercadopago: 'Configurá las cuentas de cobro de Mercado Pago.',
    shopping: 'Configurá la declaración de ventas para cada sucursal.',
    branches: 'Elegí una sucursal para configurar sus datos fiscales y facturación electrónica.',
    pos: 'Configurá el modo de venta y las terminales de cobro por sucursal.',
    currencies: 'Habilitá otras monedas además de pesos y revisá la cotización con la que se convierten.',
    plugins: 'Activá solo las funcionalidades que tu empresa usa. Al desactivar un plugin se oculta del menú y de la app, pero no se borra nada — podés volver a activarlo cuando quieras.'
  };

  return (
    <Box className="settings-page" sx={{ p: { xs: 2, md: 3 }, minHeight: '100%' }}>
      {loading ? (
        <Box sx={{ p: 3 }}>
          <p>Cargando configuraciones...</p>
        </Box>
      ) : (
        <>
        <PageHeader title="Configuración" subtitle="Administrá la empresa, sus integraciones y la operación de cada sucursal." />
        <Paper elevation={0} sx={{ mt: 3, p: { xs: 2, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', minHeight: 'calc(100vh - 220px)' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3} lg={2.25}>
            <Box sx={{ position: { md: 'sticky' }, top: 24 }}>
              <List disablePadding>{navigation.map((item) => <ListItemButton key={item.id} selected={activeSection === item.id} onClick={() => setActiveSection(item.id)} sx={{ px: 1.5, py: 1.35, mb: 0.75, minHeight: 78, height: 'auto !important', alignItems: 'flex-start', borderRadius: 2, border: '1px solid transparent', '&.Mui-selected': { bgcolor: 'transparent', borderColor: 'primary.main', color: 'text.primary' }, '&.Mui-selected:hover': { bgcolor: 'action.hover' }, '& .MuiListItemText-root': { my: 0, minWidth: 0 }, '& .MuiListItemText-primary': { display: 'block', lineHeight: 1.25 }, '& .MuiListItemText-secondary': { display: 'block', mt: 0.45, whiteSpace: 'normal', lineHeight: 1.35 } }}><ListItemIcon sx={{ minWidth: 36, mt: 0.2, color: activeSection === item.id ? 'primary.main' : 'text.secondary' }}>{item.icon}</ListItemIcon><ListItemText primary={item.label} secondary={item.helper} primaryTypographyProps={{ fontWeight: activeSection === item.id ? 700 : 500, fontSize: 14 }} secondaryTypographyProps={{ variant: 'caption' }} /></ListItemButton>)}</List>
            </Box>
          </Grid>
          <Grid item xs={12} md={9} lg={9.75}>
            <Box mb={3}>
              <Typography variant="h5" fontWeight={500}>{navigation.find((item) => item.id === activeSection)?.label}</Typography>
              <Typography color="text.secondary">{isBranchSection ? `${sectionDescriptions[activeSection]} · ${selectedLocation?.name || 'Sucursal seleccionada'}` : sectionDescriptions[activeSection]}</Typography>
              {needsLocationSelector && <FormControl size="small" sx={{ mt: 2, width: { xs: '100%', sm: 420 } }}><InputLabel>Sucursal</InputLabel><Select label="Sucursal" value={selectedLocationId} onChange={(event) => setSelectedLocationId(event.target.value)}>{locations.map((location) => <MenuItem key={location.id} value={String(location.id)}>{location.name}</MenuItem>)}</Select></FormControl>}
            </Box>
          {activeSection === 'company' && <>
            <SettingsContentCard title="Identidad visual" description="Logo y elementos que identifican a tu empresa."><LogoUpload readOnly={!canManageCompanyIdentity} /></SettingsContentCard>
          </>}

          {activeSection === 'branches' && locations.filter(loc => String(loc.id) === String(selectedLocationId)).map((location) => {
              const id = location.id;
              const name = location.name;
              const rutKey = `location_rut_${id}`;
              const razonSocialKey = `location_razon_social_${id}`;
              const companyAddressKey = `location_company_address_${id}`;
              const companyNameKey = `location_company_name_${id}`;
              const returnWindowKey = 'sales_return_window_days';

              return (
                <SettingsContentCard key={id} title="Datos fiscales" description="Información usada por esta sucursal para facturar.">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="RUT"
                          fullWidth
                          value={settings[rutKey] || ''}
                          onChange={(e) => handleChange(rutKey, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Razón Social"
                          fullWidth
                          value={settings[razonSocialKey] || ''}
                          onChange={(e) => handleChange(razonSocialKey, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Dirección Fiscal"
                          fullWidth
                          value={settings[companyAddressKey] || ''}
                          onChange={(e) => handleChange(companyAddressKey, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Nombre de la Empresa"
                          fullWidth
                          value={settings[companyNameKey] || ''}
                          onChange={(e) => handleChange(companyNameKey, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Plazo de devolución (días)"
                          type="number"
                          fullWidth
                          value={settings[returnWindowKey] || '30'}
                          helperText="Usá 0 para permitir solo el mismo día."
                          inputProps={{ min: 0, step: 1 }}
                          onChange={(e) => handleChange(returnWindowKey, e.target.value)}
                        />
                      </Grid>
                    </Grid>
                </SettingsContentCard>
              );
            })}

          {activeSection === 'branches' && <Box sx={{ mt: 3 }}>{renderIntegrationCard('electronic_billing')}</Box>}

          {activeSection === 'mercadopago' && <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={500}>Cuenta de Mercado Pago</Typography>
              <Typography variant="body2" color="text.secondary">Credenciales de la cuenta utilizada para procesar cobros.</Typography>
            </Box>
            {renderIntegrationCard('mercadopago', INTEGRATIONS.mercadopago)}
          </>}

          {activeSection === 'shopping' && <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={500}>Integración con Shopping</Typography>
              <Typography variant="body2" color="text.secondary">Configuración de declaración de ventas para la sucursal elegida.</Typography>
            </Box>
            {renderIntegrationCard('shopping')}
          </>}

          {activeSection === 'pos' && <>
            {selectedLocation && <SettingsContentCard title="Modo de venta" description="Definí cómo se muestran y cargan los productos al vender en esta sucursal.">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Vista de ventas</InputLabel>
                    <Select
                      label="Vista de ventas"
                      value={settings[`location_sales_view_mode_${selectedLocation.id}`] || 'touch'}
                      onChange={(event) => handleSalesViewModeChange(selectedLocation.id, event.target.value)}
                      disabled={saving}
                    >
                      <MenuItem value="touch">Categorías y productos (táctil)</MenuItem>
                      <MenuItem value="retail">Retail (escáner / código de barras)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Los cambios se guardan automáticamente.</Typography>
                </Grid>
              </Grid>
            </SettingsContentCard>}
            <Box sx={{ mt: 3 }}>
              <PosIntegrationSettingsTab locationId={selectedLocationId} locationName={selectedLocation?.name} readOnly={!canModifySettings} />
            </Box>
          </>}
          {activeSection === 'currencies' && multiCurrencyEnabled && <CurrencySettingsTab readOnly={!canModifySettings} />}
          {activeSection === 'plugins' && <PluginsSettingsTab readOnly={!canManagePlugins} />}
          {activeSection !== 'pos' && activeSection !== 'company' && activeSection !== 'plugins' && activeSection !== 'currencies' && <Box display="flex" justifyContent="flex-end" mt={3}><LoadingButton variant="contained" onClick={handleSave} loading={saving} sx={{ px: 4 }}>Guardar cambios</LoadingButton></Box>}
          </Grid>
        </Grid>
        </Paper>
        </>
      )}

      <Snackbar
        open={successOpen}
        autoHideDuration={5000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
          ¡Configuración guardada correctamente!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
